import React, { forwardRef, useEffect } from 'react';
import cn from 'classnames';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { NavLink } from 'react-router-dom';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Tooltip from '@material-ui/core/Tooltip';
import Collapse from '@material-ui/core/Collapse';
import IconExpandLess from '@material-ui/icons/ExpandLess';
import IconExpandMore from '@material-ui/icons/ExpandMore';
import IconSpacer from '@material-ui/icons/FiberManualRecord';
import { updateURLQueryString } from 'sagas/user/help';
import { useTranslation } from 'react-i18next';
import Proptypes from 'prop-types';
// ----------------------------------------------------------------------
// eslint-disable-next-line react/display-name
export const ListItemLink = forwardRef((props, ref) => (
  <NavLink exact {...props} innerRef={ref} />
));

// Can be a link, or button
// eslint-disable-next-line react/display-name
export const ListItemComponent = forwardRef((props, ref) => {
  // Omit isCollapsed
  /* eslint react/prop-types: 0 */
  const { isCollapsed, ...newProps } = props;
  const classes = useStyles();

  const updateUrl = url => {
    updateURLQueryString(url);
  };
  const component =
    typeof props.link === 'string' ? (
      <ListItem
        {...newProps}
        button
        component={ListItemLink}
        to={props.link}
        disableRipple
        disableTouchRipple
        onClick={() => updateUrl(props.link)}
      />
    ) : (
      <ListItem {...newProps} button disableRipple disableTouchRipple />
    );

  return (
    <div
      ref={ref}
      className={cn({
        [classes.navItemCollapsedWrapper]: isCollapsed,
      })}
    >
      {component}
    </div>
  );
});

const SidebarNavItem = props => {
  const {
    name,
    themeID,
    link,
    Icon,
    IconStyles = {},
    IconClassName = '',
    isCollapsed,
    // isNested,
    nestingLevel = 0,
    nestingOffset = 16,
    className,
    changeThemeCb,
    items = [],
    subMenu,
  } = props;
  const { t } = useTranslation();
  const isTooltipEnabeld = isCollapsed;
  const storedTheme = localStorage.getItem('theme');
  const classes = useStyles();
  const hasChildren = subMenu && items && items.length > 0;
  // Flattened array of all children
  const getItemsAll = items => {
    return items.reduce((allItems, item) => {
      // let res = allItems.concat([item])

      if (item.items && item.items.length) {
        return allItems.concat([item], getItemsAll(item.items));
      } else {
        return allItems.concat([item]);
      }
    }, []);
  };

  const itemsAll = getItemsAll(items);
  const foundPath = itemsAll.some(item => {
    return item.link === window.location.pathname;
  });
  const hasChildrenAndIsActive = hasChildren && foundPath;

  const isOpen = hasChildrenAndIsActive || false;

  const [open, setOpen] = React.useState(isOpen);

  useEffect(() => {
    if (isOpen && foundPath) {
      // if the path is found and isOpen is set to true, open the collapse component
      setOpen(true);
    }
  }, [foundPath, isOpen]);

  const handleClick = () => {
    // this callback is for changing the theme
    if (typeof changeThemeCb === 'function') {
      changeThemeCb(themeID);
      localStorage.setItem('theme', themeID);
    }
    setOpen(!open);
  };

  const ListItemIconInner =
    (Boolean(Icon) && (
      // <IconButton className={classes.iconContainer}>
      <Icon className={classes.mySvgStyle} />
      // </IconButton>
    )) ||
    (isCollapsed && (
      <IconSpacer
        classes={{ root: classes.iconSpacerColor }}
        className={classes.iconSpacer}
      />
    )) ||
    '';

  const nestingOffsetChildren = !isCollapsed ? nestingOffset + 16 : 16;

  const ListItemElement = (
    <ListItemComponent
      link={link}
      className={cn(
        classes.navItem,
        { [classes.navItemCollapsed]: isCollapsed },
        // isNested && !isCollapsed && classes.nested,
        // { ['active']: hasChildrenAndIsActive },

        className,
      )}
      style={{
        fontSize: `${1 - 0.07 * nestingLevel}em`,
        paddingLeft: `${
          !ListItemIconInner ? nestingOffset + 40 : nestingOffset
        }px`,
      }}
      isCollapsed={isCollapsed}
      onClick={handleClick}
    >
      {Boolean(ListItemIconInner) && (
        <ListItemIcon
          style={IconStyles}
          className={cn(classes.navItemIcon, IconClassName)}
        >
          {ListItemIconInner}
        </ListItemIcon>
      )}
      <ListItemText
        primary={subMenu ? t(subMenu) : t(name)}
        disableTypography={true}
        className={cn(classes.navItem, { theme: themeID === storedTheme })}
      />
      {hasChildren && !open && (
        <IconExpandMore className={classes.iconToggle} />
      )}
      {hasChildren && open && <IconExpandLess className={classes.iconToggle} />}
    </ListItemComponent>
  );

  const ListItemRoot = isTooltipEnabeld ? (
    <Tooltip
      disableFocusListener={!isTooltipEnabeld}
      disableHoverListener={!isTooltipEnabeld}
      disableTouchListener={!isTooltipEnabeld}
      title={subMenu ? t(subMenu) : t(name)}
      placement="right"
    >
      {ListItemElement}
    </Tooltip>
  ) : (
    ListItemElement
  );

  const ListItemChildren = hasChildren ? (
    <div className={cn(classes.navItemChildren)}>
      <Collapse in={open} timeout="auto">
        {/* <Divider /> */}
        <List component="div" disablePadding>
          {items.map(item => (
            <SidebarNavItem
              {...item}
              isNested={true}
              nestingLevel={nestingLevel + 1}
              isCollapsed={isCollapsed}
              key={item.name || item.link}
              isOpen={open}
              nestingOffset={nestingOffsetChildren}
            />
          ))}
        </List>
      </Collapse>
    </div>
  ) : null;

  return (
    <div
      className={cn(
        { [classes.navItemWrapperActive]: hasChildrenAndIsActive },
        {
          [classes.navItemWrapperActiveCollapsed]:
            hasChildrenAndIsActive && isCollapsed,
        },
      )}
    >
      {ListItemRoot}
      {ListItemChildren}
    </div>
  );
};

const useStyles = makeStyles(theme =>
  createStyles({
    // nested: {
    //   paddingLeft: theme.spacing(10),
    // },
    navItemWrapper: {
      position: 'relative',
    },
    navItemWrapperActive: {
      // background: 'rgba(0, 0, 0, 0.08)',
    },
    navItemWrapperActiveCollapsed: {
      // background: 'rgba(0, 0, 0, 0.08)',
    },
    navItem: {
      position: 'relative',

      '&.active': {
        color: theme.palette.secondary.light,
        background: theme.palette.primary.main,
        '& $iconSpacer': {
          color: theme.palette.secondary.light,
        },
        '& $mySvgStyle': {
          cursor: 'pointer',
          color: `${theme.palette.secondary.light} !important`,
        },
        '& .MuiListItemIcon-root': {
          color: theme.palette.text.primary,
        },
      },
      '& .theme': {
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        maxWidth: 'fit-content',
      },
    },
    navItemChildren: {},
    navItemCollapsed: {
      whiteSpace: 'nowrap',
      flexWrap: 'nowrap',
      width: theme.sidebar.widthCollapsed,
      '& $iconToggle': {
        position: 'absolute',
        bottom: -5,
        fontSize: 14,
        left: '50%',
        marginLeft: '-0.5em',
      },
      '&.active': {
        // background: 'rgba(0, 0, 0, 0.08)',
        background: theme.palette.primary.main,
      },
    },
    navItemCollapsedWrapper: {
      width: theme?.sidebar?.widthCollapsed,
    },
    navItemIcon: {
      minWidth: 40,
      color: theme.palette.secondary.light,
    },
    mySvgStyle: {
      color: theme.palette.text.primary,
    },
    iconToggle: {},
    iconSpacer: {
      fontSize: 13,
      marginLeft: 0,
    },
    iconSpacerColor: {
      color: theme.palette.primary.main,
    },
  }),
);
SidebarNavItem.propTypes = {
  name: Proptypes.string,
  themeID: Proptypes.string,
  link: Proptypes.string,
  Icon: Proptypes.elementType,
  IconStyles: Proptypes.string,
  IconClassName: Proptypes.string,
  isCollapsed: Proptypes.bool,
  nestingLevel: Proptypes.number,
  nestingOffset: Proptypes.number,
  className: Proptypes.string,
  changeThemeCb: Proptypes.func,
  items: Proptypes.array,
  subMenu: Proptypes.string,
};
export default SidebarNavItem;
